import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useCartContext } from '../../context'
import { useScrollLock } from '../../hooks'
import { startCheckout } from '../../services'
import { Button } from '../Button'
import styles from './CartDrawer.module.css'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { items, itemCount, subtotal, discount, total, updateQuantity, removeItem } =
    useCartContext()
  const drawerRef = useRef<HTMLDivElement>(null)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const handleCheckout = async () => {
    setCheckoutError(null)
    setIsCheckingOut(true)
    try {
      await startCheckout(items)
      // En caso de éxito el navegador redirige a Stripe, así que no se vuelve aquí.
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : 'No se pudo iniciar el pago.')
      setIsCheckingOut(false)
    }
  }

  useScrollLock(isOpen)

  useEffect(() => {
    if (isOpen) {
      drawerRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <aside
        ref={drawerRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label="Carrito de compras"
        tabIndex={-1}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Carrito ({itemCount})</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar carrito"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className={styles.content}>
          {items.length === 0 ? (
            <div className={styles.empty}>
              <p>Tu carrito está vacío</p>
              <Button variant="outline" onClick={onClose}>
                Continuar comprando
              </Button>
            </div>
          ) : (
            <ul className={styles.items}>
              {items.map((item) => (
                <li key={item.product.id} className={styles.item}>
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className={styles.itemImage}
                    onError={(e) => {
                      e.currentTarget.src = '/images/placeholder.svg'
                    }}
                  />
                  <div className={styles.itemDetails}>
                    <h3 className={styles.itemName}>{item.product.name}</h3>
                    <p className={styles.itemPrice}>${item.product.price.toFixed(2)}</p>
                    <div className={styles.quantity}>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        aria-label="Reducir cantidad"
                      >
                        -
                      </button>
                      <span className={styles.quantityValue}>{item.quantity}</span>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        aria-label="Aumentar cantidad"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => removeItem(item.product.id)}
                    aria-label={`Eliminar ${item.product.name} del carrito`}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <footer className={styles.footer}>
            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span>Subtotal</span>
                <span>${(subtotal || total).toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className={`${styles.summaryRow} ${styles.discount}`}>
                  <span>Descuento de Bundle (15%)</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className={styles.totalRow}>
                <span>Total</span>
                <span className={styles.totalPrice}>${total.toFixed(2)}</span>
              </div>
            </div>
            {checkoutError && (
              <p className={styles.checkoutError} role="alert">
                {checkoutError}
              </p>
            )}
            <Button fullWidth size="lg" onClick={handleCheckout} disabled={isCheckingOut}>
              {isCheckingOut ? 'Redirigiendo a pago…' : 'Finalizar Compra'}
            </Button>
          </footer>
        )}
      </aside>
    </>,
    document.body
  )
}
